import { useState } from 'react';
import { Box, Flex, HStack } from '@chakra-ui/react';
import { useUpload } from 'hooks/useFiles';
import { useToast, useUpdateProduct } from 'hooks';
import { Modal, ModalBody, Button, Progress, ModalHeader } from 'components/UI';
import ProductField from '../ProductField';
import { IProductResource } from '../../../../server/types';

type UpdateThumbnailProps = {
  productId: string;
  directory: string;
  onCancelUploadClick(arg0?: IProductResource): void;
  displayName: string;
};

const UpdateThumbnail = ({
  productId,
  directory = 'thumbnail',
  onCancelUploadClick,
  displayName = 'Thumbnail Image',
}: UpdateThumbnailProps) => {
  const [errorMessage, setErrorMessage] = useState<string | Record<string, never> | null>();

  const [thumbnailImage, setThumbnailImage] = useState();
  const [thumbnailFile, setThumbnailFile] = useState<Blob | string | undefined>();

  const [, fileUpload, { progress }] = useUpload();
  const [updateProduct, { loading: updateProductLoading }] = useUpdateProduct();
  const [showToast] = useToast();

  const updateFile = (
    fileStateProp: string,
    newFile: Blob | undefined,
    objectNameProp: any,
    objectName: React.SetStateAction<undefined>
  ) => {
    setThumbnailFile(newFile);
    setThumbnailImage(objectName);
  };

  const onUploadClick = async () => {
    if (thumbnailFile !== null) {
      try {
        const formData = new FormData();

        formData.set('directory', `temp/products/${productId}/${directory}`);
        formData.set('productThumbnail', thumbnailFile as string);

        const fileSize = (thumbnailFile as Blob).size / 1024 / 1024;

        if (fileSize > 10) {
          setErrorMessage('The thumbnail selected is too large (max file size is 10 MB).');
          return;
        }

        const { productThumbnail } = await fileUpload(formData);

        if (productThumbnail?.[0]?.key) {
          const updatedProduct = await updateProduct(
            productId,
            directory === 'referenceImage'
              ? { refImage: productThumbnail[0].key }
              : { thumbnailImage: productThumbnail[0].key }
          );
          onCancelUploadClick(updatedProduct as IProductResource);
          showToast({
            status: 'success',
            message: 'File uploaded successfully!',
          });
        }
      } catch (error) {
        showToast({
          status: 'error',
          message: 'File upload failed.',
        });
        setErrorMessage(error as string);
      }
    } else {
      setErrorMessage(null);
    }
  };

  const renderErrorMessage = errorMessage ? <Box color="red">{errorMessage as string}</Box> : null;

  const renderProgress = progress > 0 ? <Progress value={progress} /> : null;

  return (
    <Modal
      isOpen
      isCloseButton
      closeOnEsc={false}
      closeOnOverlayClick
      isCentered
      size="md"
      onClose={() => onCancelUploadClick()}
    >
      <ModalHeader
        sx={{
          fontSize: 'md',
          px: 6,
          pt: 4,
          pb: 2,
          fontWeight: 'bold',
          letterSpacing: '0',
        }}
      >
        SELECT A FILE
      </ModalHeader>
      <ModalBody>
        <Flex flexDir="column">
          <Box
            sx={{
              bgColor: 'base2Colour.400',
              mb: 1.5,
              p: 2.5,
            }}
          >
            <Flex
              sx={{
                flex: 1,
                flexDir: 'column',
                justifyContent: 'column',
                bgColor: 'base2Colour.500',
                m: 1.5,
                alignItems: 'space-between',
                fontSize: 'sm',
                color: 'base2FontColour.500',
              }}
            >
              <ProductField
                name="thumbnailImage"
                displayName={displayName}
                buttonText="Select"
                edit
                fieldType="fileSelect"
                onChange={updateFile}
                fileStateProp="thumbnailFile"
                data={thumbnailImage}
              />
            </Flex>
            <Box display="flex" flexDir="row" justifyContent="center">
              {renderProgress}
            </Box>
          </Box>
        </Flex>
        <HStack
          sx={{
            justifyContent: 'center',
            gap: 2,
            mt: 1,
            mb: 2,
          }}
        >
          <Button size="sm" isDisabled={!thumbnailImage} onClick={onUploadClick} isLoading={updateProductLoading}>
            UPLOAD
          </Button>
          <Button
            size="sm"
            onClick={() => {
              onCancelUploadClick();
            }}
          >
            CANCEL
          </Button>
        </HStack>

        {renderErrorMessage}
      </ModalBody>
    </Modal>
  );
};

export default UpdateThumbnail;
